import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import '../styles/site-header.css'

// Mirrors the <header> markup in the static pages (public/index.html etc.)
// exactly — same classes, same wordmark logo, same links, same mobile
// hamburger menu — so the site reads as one continuous nav whether you're
// on a static page or in this SPA. The logo MUST stay in sync with the
// static pages' — a differently-sized wordmark here shifts the whole nav's
// width, producing a visible jump when navigating between a static page
// and this app.
// Static pages never mark themselves active in the nav (they can't — each
// is a separate HTML file with no notion of "current page"), but this app
// can, so "Insights" gets an .active state whenever the route is under
// /insights.
const NAV_LINKS = [
  { href: '/financial-advisory', label: 'Financial Advisory' },
  { href: '/industries', label: 'Industries' },
  { href: '/transactions', label: 'Our Transactions' },
  { href: '/approach', label: 'Approach' },
  { href: '/insights', label: 'Insights' },
]

export default function SiteHeader() {
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  // Close on every route change — this component doesn't remount between
  // /insights and /insights/admin, so a menu left open would otherwise
  // persist across the navigation.
  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    function handleResize() {
      if (window.innerWidth > 900) setMenuOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', handleResize)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleResize)
    }
  }, [menuOpen])

  return (
    <header>
      <div className="nav">
        <a href="/" className="wordmark" aria-label="Selcius Partners">
          <img
            src="/selcius-logo.png"
            alt="Selcius Partners"
            style={{ display: 'block', height: 40, width: 'auto' }}
          />
        </a>
        <nav className={menuOpen ? 'navlinks open' : 'navlinks'} id="navlinks">
          {NAV_LINKS.map(link => (
            <a
              key={link.href}
              href={link.href}
              className={pathname.startsWith('/insights') && link.href === '/insights' ? 'active' : undefined}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="nav-actions">
          <a href="/contact" className="nav-cta">
            Inquire
          </a>
          <button
            type="button"
            className="navlinks-mobile-btn"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="navlinks"
            onClick={() => setMenuOpen(open => !open)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M2 5h16M2 10h16M2 15h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  )
}
